import React, { useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { SnackbarProvider } from "notistack";
import { AlertProvider } from "./context/AlertContext";
import { ThemeProvider } from "./context/ThemeContext";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";

import Layout from "./components/layout/Layout";
import AppRoutes from "./routes";
import { useAuth0 } from "@auth0/auth0-react";
import { instance } from "./services/api";

// Loading component
const LoadingScreen = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" sx={{ mt: 2 }}>
      Loading application...
    </Typography>
  </Box>
);

// Error component
const ErrorScreen = ({ message }: { message: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      padding: 3,
    }}
  >
    <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
      <Typography variant="h6">Authentication Error</Typography>
      <Typography variant="body1">{message}</Typography>
    </Alert>
  </Box>
);

const App: React.FC = () => {
  const {
    error: auth0Error,
    isLoading: auth0Loading,
    getAccessTokenSilently,
  } = useAuth0();

  // Configure axios with Auth0 token only once when authenticated
  useEffect(() => {
    const interceptor = instance.interceptors.request.use(
      async (config) => {
        const token = await getAccessTokenSilently();
        config.headers.Authorization = `Bearer ${token}`;

        return config;
      },
      (error) => Promise.reject(error),
    );

    return () => {
      instance.interceptors.request.eject(interceptor);
    };
  }, [getAccessTokenSilently]);

  if (auth0Error) {
    return <ErrorScreen message={auth0Error.message} />;
  }

  if (auth0Loading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
      >
        <AlertProvider>
          <Layout>
            <AppRoutes />
          </Layout>
        </AlertProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
